import { Box, Typography, Chip, useTheme, alpha } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

const val = (v) => (v == null || v === "" ? "—" : String(v));

const plColorOf = (v, theme) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return theme.palette.text.secondary;
  if (n > 0) return theme.palette.profitColor ?? "#18b009";
  if (n < 0) return theme.palette.lossColor ?? "#fa2104";
  return theme.palette.text.secondary;
};

/**
 * One sale-engine holding. The grid has ten columns, which leaves ~34px each on a
 * phone — unreadable — so every field moves onto a card instead.
 */
export const SaleHoldingCard = ({ row }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const sold = row.isSold === true;
  const headlinePL = sold && row.actualPL != null ? row.actualPL : row.curPL;
  const headlineColor = plColorOf(headlinePL, theme);

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1,
        border: `1px solid ${alpha(primary, 0.14)}`,
        borderLeft: `3px solid ${alpha(headlineColor, 0.85)}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography noWrap title={row.symbol} sx={{ fontWeight: 800, fontSize: "0.82rem", minWidth: 0 }}>
          {row.symbol}
        </Typography>
        <Chip
          size="small"
          label={sold ? "Sold" : row.orderState === "pending" ? "Order pending" : row.triggered ? "Trigger watch" : "Watching"}
          color={sold ? "success" : row.orderState === "pending" ? "warning" : row.triggered ? "info" : "default"}
          variant={sold || row.orderState === "pending" || row.triggered ? "filled" : "outlined"}
          sx={{ height: 19, fontSize: "0.62rem", fontWeight: 700, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: headlineColor, whiteSpace: "nowrap" }}>
          {val(headlinePL)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.3 }}>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          {val(row.qty)} × {val(row.buyPrice)}
        </Typography>
        <ArrowForward sx={{ fontSize: 12, color: "text.disabled" }} />
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 700 }}>
          {val(row.marketPrice)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 0.5,
          mt: 0.75,
          pt: 0.75,
          borderTop: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        {[
          { k: row.triggered ? "Next trig" : "Target", v: val(row.stopProfit) },
          { k: row.triggered ? "Locked exit" : "Stop", v: val(row.stopLoss) },
          { k: "Sale", v: val(row.salePrice) },
          { k: "Est P/L", v: row.estPL == null ? "—" : row.estPLAlt != null ? `${row.estPL} / ${row.estPLAlt}` : val(row.estPL), c: plColorOf(row.estPL, theme) },
        ].map(({ k, v, c }) => (
          <Box key={k} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.58rem", color: "text.secondary", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {k}
            </Typography>
            <Typography noWrap title={v} sx={{ fontSize: "0.74rem", fontWeight: 700, color: c || "text.primary" }}>
              {v}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SaleHoldingCard;
